import { ContestType } from '@betterpool/api-types/contests-service';
import { useCallback, useMemo } from 'react';

import { getTeamOrPlayerImageUrl } from '~/domains/common/utils/getTeamOrPlayerImageUrl';
import usePlayerPropContestEntriesWithPicks from '~/domains/contest/hooks/usePlayerPropContestEntriesWithPicks';
import type { FullContestPlayerSurvivor } from '~/domains/contest/domains/services/interfaces/full-contest.interface';

import { useContestContext } from '../../common/context/ContestContext/ContestContext';
import { useSlatesContext } from '../../common/context/SlatesContext/SlatesContext';
import type { GamecenterPlayerEntryProps } from '../../playerTier/components/EntryScheduledSingleSlate/EntryScheduledSingleSlate';
import { getEntryPickState } from '../../common/components/GamecenterEntriesPageScheduled/GamecenterEntry/entryPickState';

function usePlayerEntriesScheduled() {
  const { contest } = useContestContext();
  const { slates } = useSlatesContext([ContestType.PLAYER_SURVIVOR]);

  const { data, isLoading, error, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    usePlayerPropContestEntriesWithPicks({
      contestId: contest.id,
    });

  const getExpectedPicksCount = useCallback(
    (slate) => {
      if (contest.settings?.stepUp?.slates?.length > 0 && slate?.id) {
        const slateConfig = contest.settings.stepUp.slates.find(
          (slateSettings) => slateSettings.slateId === slate.id
        );
        if (slateConfig && typeof slateConfig.expectedPicksCount === 'number') {
          return slateConfig.expectedPicksCount;
        }
      }

      if (slate?.settings?.expectedPicksCount) {
        return slate.settings.expectedPicksCount;
      }

      return 1;
    },
    [contest.settings?.stepUp?.slates]
  );

  const entriesItems = useMemo(() => {
    if (isLoading || isError || !data) return [];

    return data.pages.flatMap(({ entries, picks }) =>
      entries.map((entry) => {
        const entryPicks = picks.entries.find((e) => e.id === entry.id);

        return {
          id: entry.id,
          name: entry.name,
          entryOrder: entry.entryOrder,
          isMultiOwner: entry.isMultiOwner,
          pickState: getEntryPickState(
            entry.isPickController,
            slates.map((slate) => {
              const slatePicks = entryPicks?.slates.find((candidate) => candidate.id === slate.id);
              return {
                picks: slatePicks?.picks ?? [],
                isMissingPicks: slatePicks?.isMissingPicks,
              };
            })
          ),
          slates: slates.map((slate) => {
            const slatePicks = entryPicks?.slates.find((s) => s.id === slate.id);
            const slateAlerts = slatePicks?.alerts;

            return {
              id: slate.id,
              name: slate.slate.name,
              picks: new Array(getExpectedPicksCount(slate)).fill(null).map((_, index) => {
                const pick = slatePicks?.picks?.[index];
                if (!pick) return null;

                return {
                  id: pick.playerId,
                  name: pick.playerName,
                  image: getTeamOrPlayerImageUrl(contest.league.alias, pick.playerId),
                };
              }),
              isUpcoming: slate.slate?.isUpcoming,
              isMakePicks: slate.slate.isPicksheetAvailable,
              alerts: slateAlerts,
              isMissingPicks: slatePicks?.isMissingPicks,
              requiredPicksCount: getExpectedPicksCount(slate),
            };
          }),
        } satisfies GamecenterPlayerEntryProps;
      })
    );
  }, [contest, data, getExpectedPicksCount, isError, isLoading, slates]);

  return {
    entries: entriesItems,
    fetchNextPage,
    hasNextPage,
    isError,
    error,
    isFetchingNextPage,
    isLoading,
  };
}

export default usePlayerEntriesScheduled;
